/**
 * Notification preferences — self-service for the signed-in admin/operator.
 *
 * Reads + mutates the caller's own preference row (auto-created on first GET).
 * Surfaces:
 *   - Master "Notifications enabled" kill switch.
 *   - DND window (start / end hour).
 *   - Per-channel toggles (push / email / SMS / WhatsApp).
 *   - Per-type toggles (panic / notice / bill / visitor / complaint / event /
 *     ev_charging / poll / booking) for the push channel — finer-grained
 *     toggles are exposed on mobile (this surface keeps the admin web tidy).
 *   - Panic bypass DND toggle (with a warning when disabled).
 *   - Reset to defaults.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, RotateCcw, Save, BellOff, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { get, patch, post, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

type TypePref = Partial<Record<'push' | 'email' | 'sms' | 'whatsapp', boolean>>;
type TypePrefsMap = Record<string, TypePref>;

interface PrefRow {
  id: string;
  user_id: string;
  society_id: string | null;
  notifications_enabled: boolean;
  do_not_disturb_enabled: boolean;
  dnd_start_hour: number | null;
  dnd_end_hour: number | null;
  channel_push: boolean;
  channel_email: boolean;
  channel_sms: boolean;
  channel_whatsapp: boolean;
  type_preferences: TypePrefsMap | null;
  panic_bypass_dnd: boolean;
  created_at: string;
  updated_at: string;
}

interface PatchBody {
  notifications_enabled?: boolean;
  do_not_disturb_enabled?: boolean;
  dnd_start_hour?: number | null;
  dnd_end_hour?: number | null;
  channel_push?: boolean;
  channel_email?: boolean;
  channel_sms?: boolean;
  channel_whatsapp?: boolean;
  type_preferences?: TypePrefsMap | null;
  panic_bypass_dnd?: boolean;
}

const TYPE_KEYS: ReadonlyArray<{ key: string; label: string; description: string }> = [
  { key: 'panic', label: 'Panic / SOS', description: 'Emergency alerts. Strongly recommended on.' },
  { key: 'notice', label: 'Notices', description: 'Society announcements + circulars.' },
  { key: 'bill', label: 'Bills + receipts', description: 'New bills, payment confirmations.' },
  { key: 'bill_reminder', label: 'Bill reminders', description: 'Defaulter / due-date reminders.' },
  { key: 'visitor', label: 'Visitors', description: 'Visitor arrival + approval requests.' },
  { key: 'complaint', label: 'Complaints', description: 'New complaints + status updates.' },
  { key: 'event', label: 'Events', description: 'Society events + RSVP reminders.' },
  { key: 'booking', label: 'Amenity bookings', description: 'Booking confirmations + reminders.' },
  { key: 'ev_charging', label: 'EV charging', description: 'Charge complete, overstay warnings.' },
  { key: 'parking_dispute', label: 'Parking disputes', description: 'Wrong-parker alerts, disputes.' },
  { key: 'poll', label: 'Polls + community', description: 'New polls, posts, lost & found.' },
  { key: 'governance', label: 'Governance', description: 'AGM / EGM / proposal updates.' }
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

export function NotificationPreferencesPage(): JSX.Element {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['me', 'notification-preferences'],
    queryFn: () => get<PrefRow>('/me/notification-preferences')
  });

  const update = useMutation({
    mutationFn: (body: PatchBody) => patch<PrefRow, PatchBody>('/me/notification-preferences', body),
    onSuccess: (next) => {
      qc.setQueryData(['me', 'notification-preferences'], next);
      toast.success('Preferences updated');
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const reset = useMutation({
    mutationFn: () => post<PrefRow>('/me/notification-preferences/reset'),
    onSuccess: (next) => {
      qc.setQueryData(['me', 'notification-preferences'], next);
      toast.success('Restored defaults');
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const pref = query.data;

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="Notification preferences" description="Loading…" />
        <div className="mx-auto w-full max-w-3xl space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      </>
    );
  }

  if (query.isError || !pref) {
    return (
      <>
        <PageHeader title="Notification preferences" />
        <div className="mx-auto w-full max-w-3xl px-vt-6 pb-vt-8 pt-vt-4">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4 text-vt-text-muted">
              <AlertTriangle className="h-5 w-5 text-vt-alert" />
              <span>Could not load preferences. Please retry.</span>
              <Button size="sm" variant="secondary" onClick={() => void query.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Notification preferences"
        description="Choose how and when HoodDesk reaches you."
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => reset.mutate()}
            loading={reset.isPending}
            loadingText="Resetting…"
          >
            <RotateCcw className="h-4 w-4" /> Reset to defaults
          </Button>
        }
      />
      <div className="mx-auto w-full max-w-3xl space-y-vt-6 px-vt-6 pb-vt-8 pt-vt-4">
        <MasterCard pref={pref} update={update.mutate} />
        <DndCard pref={pref} update={update.mutate} />
        <ChannelsCard pref={pref} update={update.mutate} />
        <TypesCard pref={pref} update={update.mutate} />
      </div>
    </>
  );
}

function MasterCard({
  pref,
  update
}: {
  pref: PrefRow;
  update: (b: PatchBody) => void;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Master switch</CardTitle>
            <CardDescription>
              When off, no notifications are sent. Even panic alerts are suppressed — use with care.
            </CardDescription>
          </div>
          <Badge variant={pref.notifications_enabled ? 'ok' : 'muted'} className="uppercase">
            {pref.notifications_enabled ? 'On' : 'Off'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <label className="flex items-center gap-vt-3 text-vt-13 text-vt-text">
          <input
            type="checkbox"
            checked={pref.notifications_enabled}
            onChange={(e) => update({ notifications_enabled: e.target.checked })}
            className="h-4 w-4"
          />
          {pref.notifications_enabled ? (
            <Bell className="h-4 w-4 text-vt-primary" />
          ) : (
            <BellOff className="h-4 w-4 text-vt-text-muted" />
          )}
          Notifications enabled
        </label>
      </CardContent>
    </Card>
  );
}

function DndCard({
  pref,
  update
}: {
  pref: PrefRow;
  update: (b: PatchBody) => void;
}): JSX.Element {
  const [start, setStart] = useState<number>(pref.dnd_start_hour ?? 22);
  const [end, setEnd] = useState<number>(pref.dnd_end_hour ?? 7);
  useEffect(() => {
    setStart(pref.dnd_start_hour ?? 22);
    setEnd(pref.dnd_end_hour ?? 7);
  }, [pref.dnd_start_hour, pref.dnd_end_hour]);

  const dirty =
    start !== (pref.dnd_start_hour ?? 22) || end !== (pref.dnd_end_hour ?? 7);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Do not disturb</CardTitle>
        <CardDescription>
          Silence non-urgent push during a window. Panic alerts bypass DND by default — see below.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-vt-4">
        <label className="flex items-center gap-vt-3 text-vt-13 text-vt-text">
          <input
            type="checkbox"
            checked={pref.do_not_disturb_enabled}
            onChange={(e) =>
              update({
                do_not_disturb_enabled: e.target.checked,
                ...(e.target.checked && pref.dnd_start_hour == null
                  ? { dnd_start_hour: 22 }
                  : {}),
                ...(e.target.checked && pref.dnd_end_hour == null ? { dnd_end_hour: 7 } : {})
              })
            }
            className="h-4 w-4"
          />
          Enable do not disturb
        </label>

        <div className="grid grid-cols-2 gap-vt-3">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="dnd-start">Start hour</Label>
            <Select
              id="dnd-start"
              value={start}
              onChange={(e) => setStart(Number(e.target.value))}
              disabled={!pref.do_not_disturb_enabled}
            >
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>
                  {h.toString().padStart(2, '0')}:00
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="dnd-end">End hour</Label>
            <Select
              id="dnd-end"
              value={end}
              onChange={(e) => setEnd(Number(e.target.value))}
              disabled={!pref.do_not_disturb_enabled}
            >
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>
                  {h.toString().padStart(2, '0')}:00
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-end">
          <Button
            size="sm"
            disabled={!dirty || !pref.do_not_disturb_enabled}
            onClick={() => update({ dnd_start_hour: start, dnd_end_hour: end })}
          >
            <Save className="h-4 w-4" /> Save DND window
          </Button>
        </div>

        <label className="mt-vt-2 flex items-start gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface-2/40 p-vt-3 text-vt-13 text-vt-text">
          <input
            type="checkbox"
            checked={pref.panic_bypass_dnd}
            onChange={(e) => update({ panic_bypass_dnd: e.target.checked })}
            className="mt-vt-1 h-4 w-4"
          />
          <span className="flex flex-col">
            <span className="font-medium">Let panic alerts bypass DND</span>
            <span className="text-vt-12 text-vt-text-muted">
              Recommended on. When off, panic / SOS notifications are silenced during DND
              — you may miss safety-critical events.
            </span>
          </span>
        </label>
      </CardContent>
    </Card>
  );
}

function ChannelsCard({
  pref,
  update
}: {
  pref: PrefRow;
  update: (b: PatchBody) => void;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Channels</CardTitle>
        <CardDescription>
          Master per-channel toggles. SMS and WhatsApp are opt-in — they incur a per-message
          cost to your society.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-vt-2">
        <ChannelRow
          label="Push notifications"
          hint="Mobile app + browser pushes."
          checked={pref.channel_push}
          onChange={(v) => update({ channel_push: v })}
        />
        <ChannelRow
          label="Email"
          hint="Receipts, monthly statements, password change confirmations."
          checked={pref.channel_email}
          onChange={(v) => update({ channel_email: v })}
        />
        <ChannelRow
          label="SMS"
          hint="OTPs always go via SMS. This toggle controls non-auth SMS."
          checked={pref.channel_sms}
          onChange={(v) => update({ channel_sms: v })}
        />
        <ChannelRow
          label="WhatsApp"
          hint="Bill reminders + notices when society has WhatsApp enabled."
          checked={pref.channel_whatsapp}
          onChange={(v) => update({ channel_whatsapp: v })}
        />
      </CardContent>
    </Card>
  );
}

function ChannelRow({
  label,
  hint,
  checked,
  onChange
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex items-start gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13 text-vt-text">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-vt-1 h-4 w-4"
      />
      <span className="flex flex-col">
        <span className="font-medium">{label}</span>
        <span className="text-vt-12 text-vt-text-muted">{hint}</span>
      </span>
    </label>
  );
}

function TypesCard({
  pref,
  update
}: {
  pref: PrefRow;
  update: (b: PatchBody) => void;
}): JSX.Element {
  const typeMap: TypePrefsMap = useMemo(() => pref.type_preferences ?? {}, [pref.type_preferences]);

  function setTypePush(key: string, value: boolean): void {
    const next: TypePrefsMap = { ...typeMap };
    next[key] = { ...(next[key] ?? {}), push: value };
    update({ type_preferences: next });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notification types (push)</CardTitle>
        <CardDescription>
          Fine-grained push toggles per category. Unset means follow the master push toggle above.
          Mobile clients expose per-channel toggles per type.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-vt-1">
        {TYPE_KEYS.map((t) => {
          const explicit = typeMap[t.key]?.push;
          const effective = explicit === undefined ? pref.channel_push : explicit;
          return (
            <label
              key={t.key}
              className="flex items-start justify-between gap-vt-3 rounded-vt-md border border-vt-border bg-vt-surface px-vt-3 py-vt-2 text-vt-13 text-vt-text"
            >
              <span className="flex flex-col">
                <span className="font-medium">{t.label}</span>
                <span className="text-vt-12 text-vt-text-muted">{t.description}</span>
              </span>
              <input
                type="checkbox"
                checked={effective}
                onChange={(e) => setTypePush(t.key, e.target.checked)}
                className="mt-vt-1 h-4 w-4"
                aria-label={`Toggle ${t.label}`}
              />
            </label>
          );
        })}
      </CardContent>
    </Card>
  );
}
